/**
 * Pipeline de lectura automatica de un calendario anual en imagen.
 * Corre 100% en el navegador (Tesseract.js, WASM, open source, sin costo ni
 * llamadas a IA - ver la decision de arquitectura acordada). El backend
 * nunca ve la imagen: solo recibe el resultado ya estructurado para su
 * revision en la tabla de staging.
 *
 * IMPORTANTE - honestidad sobre las limitaciones de este v1: la agrupacion
 * de numeros por mes (clustering) y la deteccion de marca de festivo
 * (color/forma) son heuristicas de vision por computador clasica (sin IA),
 * afinadas sin tener una imagen real de referencia. Es normal que algunos
 * dias queden mal clasificados en la primera pasada - por eso el diseño
 * completo (ver Documentacion) exige revision humana obligatoria en la
 * tabla de staging ANTES de confirmar cualquier mes. Este modulo nunca
 * "frena" el proceso por incertidumbre: ante la duda, hace su mejor lectura
 * y baja la confianza (campo `confidence`) para que se revise con prioridad.
 */
import { createWorker } from 'tesseract.js';

export interface MarkingConfig {
  /** 'color': el festivo se distingue por color de texto. 'shape': por circulo/cuadro alrededor del numero. */
  markingType: 'color' | 'shape';
  /** Color objetivo en hex (ej. "#FF0000") - solo si markingType === 'color'. */
  targetColorHex?: string;
  /** Solo si markingType === 'shape'. */
  shapeType?: 'circle' | 'square';
}

export interface ExtractedDay {
  day: number;
  month: number; // 1-12
  isMarked: boolean;
  confidence: number; // 0-1
}

interface WordBox {
  text: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const MONTH_NAMES_ES = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

function normalize(text: string): string {
  return text
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z]/g, '');
}

/** Distancia de edicion simple (Levenshtein) - para tolerar errores de OCR en el nombre del mes (ej. "ENER0"). */
function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[a.length][b.length];
}

function matchMonthName(word: string): number | null {
  const norm = normalize(word);
  if (norm.length < 4) return null;
  let bestMonth: number | null = null;
  let bestDist = Infinity;
  for (let idx = 0; idx < MONTH_NAMES_ES.length; idx++) {
    const dist = levenshtein(norm, MONTH_NAMES_ES[idx]);
    if (dist <= 2 && dist < bestDist) {
      bestDist = dist;
      bestMonth = idx + 1;
    }
  }
  return bestMonth;
}

/** Paso 1: corre Tesseract.js sobre la imagen completa y devuelve las palabras reconocidas con su posicion. */
export async function recognizeCalendarImage(imageFile: File | Blob, onProgress?: (pct: number) => void): Promise<WordBox[]> {
  const worker = await createWorker('spa', 1, {
    logger: (m) => {
      if (m.status === 'recognizing text' && onProgress) onProgress(Math.round(m.progress * 100));
    },
  });
  try {
    const { data } = await worker.recognize(imageFile, {}, { blocks: true, text: false });
    const words: WordBox[] = [];
    for (const block of data.blocks ?? []) {
      for (const paragraph of block.paragraphs) {
        for (const line of paragraph.lines) {
          for (const w of line.words) {
            words.push({ text: w.text, x0: w.bbox.x0, y0: w.bbox.y0, x1: w.bbox.x1, y1: w.bbox.y1 });
          }
        }
      }
    }
    return words;
  } finally {
    await worker.terminate();
  }
}

interface MonthHeader {
  month: number;
  cx: number;
  cy: number;
}

/** Paso 2: separa las palabras en encabezados de mes (ENERO, FEBRERO...) y candidatos a numero de dia (1-31). */
function splitWords(words: WordBox[]): { headers: MonthHeader[]; dayCandidates: (WordBox & { day: number })[] } {
  const headers: MonthHeader[] = [];
  const dayCandidates: (WordBox & { day: number })[] = [];

  for (const w of words) {
    const monthMatch = matchMonthName(w.text);
    if (monthMatch) {
      headers.push({ month: monthMatch, cx: (w.x0 + w.x1) / 2, cy: (w.y0 + w.y1) / 2 });
      continue;
    }
    const digits = w.text.replace(/[^0-9]/g, '');
    if (digits.length > 0 && digits.length <= 2) {
      const n = Number(digits);
      if (n >= 1 && n <= 31) dayCandidates.push({ ...w, day: n });
    }
  }
  return { headers, dayCandidates };
}

/**
 * Paso 3: agrupa cada numero de dia con el encabezado de mes MAS CERCANO
 * (distancia euclidiana centro a centro). Heuristica simple pero robusta
 * para el diseño de rejilla tipico de un almanaque anual (los numeros de un
 * mes estan mucho mas cerca de su propio encabezado que del de otro mes).
 * Si dos meses en el mismo almanaque tienen literalmente el mismo nombre
 * detectado dos veces (OCR duplicado), se usa el primero.
 */
function assignDaysToMonths(headers: MonthHeader[], dayCandidates: (WordBox & { day: number })[]): Map<number, (WordBox & { day: number })[]> {
  const byMonth = new Map<number, (WordBox & { day: number })[]>();
  if (headers.length === 0) return byMonth;

  for (const d of dayCandidates) {
    const cx = (d.x0 + d.x1) / 2;
    const cy = (d.y0 + d.y1) / 2;
    let closest = headers[0];
    let bestDist = Infinity;
    for (const h of headers) {
      // Los numeros SIEMPRE estan debajo de su encabezado en un almanaque -
      // penalizamos fuertemente cualquier header que quede por debajo del numero.
      const dy = cy - h.cy;
      const dx = cx - h.cx;
      const penalty = dy < 0 ? 5 : 1; // header por debajo del numero: muy improbable
      const dist = Math.sqrt(dx * dx + dy * dy) * penalty;
      if (dist < bestDist) {
        bestDist = dist;
        closest = h;
      }
    }
    const list = byMonth.get(closest.month) ?? [];
    list.push(d);
    byMonth.set(closest.month, list);
  }
  return byMonth;
}

/** Lee el color promedio (RGB) dentro de un bounding box, sobre un canvas ya cargado con la imagen. */
function averageColor(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number): [number, number, number] {
  const w = Math.max(1, Math.round(x1 - x0));
  const h = Math.max(1, Math.round(y1 - y0));
  const { data } = ctx.getImageData(Math.round(x0), Math.round(y0), w, h);
  let r = 0, g = 0, b = 0, count = 0;
  for (let i = 0; i < data.length; i += 4) {
    // Ignora pixeles casi blancos (fondo) para no diluir el color del trazo/numero.
    if (data[i] > 240 && data[i + 1] > 240 && data[i + 2] > 240) continue;
    r += data[i]; g += data[i + 1]; b += data[i + 2]; count++;
  }
  if (count === 0) return [255, 255, 255];
  return [r / count, g / count, b / count];
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
}

function colorDistance(a: [number, number, number], b: [number, number, number]): number {
  return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);
}

/**
 * Detecta si un numero esta "marcado" como festivo, leyendo pixeles reales
 * de la imagen (nunca decide solo con el texto que devolvio el OCR).
 *  - markingType 'color': compara el color promedio del texto contra el color objetivo.
 *  - markingType 'shape': mira un anillo alrededor del numero buscando un trazo
 *    (circulo/cuadro) - una version simplificada de deteccion de bordes,
 *    sin ninguna libreria de vision adicional (pura matematica de pixeles).
 */
function detectMarking(ctx: CanvasRenderingContext2D, box: WordBox, config: MarkingConfig): { isMarked: boolean; confidence: number } {
  if (config.markingType === 'color' && config.targetColorHex) {
    const avg = averageColor(ctx, box.x0, box.y0, box.x1, box.y1);
    const target = hexToRgb(config.targetColorHex);
    const black: [number, number, number] = [0, 0, 0];
    const distToTarget = colorDistance(avg, target);
    const distToBlack = colorDistance(avg, black);
    const isMarked = distToTarget < distToBlack;
    // Confianza: cuanto mas lejos esta el color promedio del "empate" entre
    // target y negro, mas seguros estamos de la clasificacion.
    const total = distToTarget + distToBlack;
    const confidence = total > 0 ? Math.min(1, Math.abs(distToTarget - distToBlack) / total + 0.5) : 0.5;
    return { isMarked, confidence };
  }

  // markingType 'shape': muestrea un anillo alrededor del bbox del numero
  // (expandido) buscando pixeles no-blancos que no sean el propio numero -
  // indicio de un circulo/cuadro dibujado alrededor.
  const w = box.x1 - box.x0;
  const h = box.y1 - box.y0;
  const marginX = w * 0.6;
  const marginY = h * 0.6;
  const outerX0 = Math.max(0, box.x0 - marginX);
  const outerY0 = Math.max(0, box.y0 - marginY);
  const outerX1 = box.x1 + marginX;
  const outerY1 = box.y1 + marginY;

  const outer = ctx.getImageData(outerX0, outerY0, outerX1 - outerX0, outerY1 - outerY0);
  const innerX0 = box.x0 - outerX0;
  const innerY0 = box.y0 - outerY0;
  const innerX1 = box.x1 - outerX0;
  const innerY1 = box.y1 - outerY0;

  let ringNonWhite = 0;
  let ringTotal = 0;
  const ow = outer.width;
  for (let y = 0; y < outer.height; y++) {
    for (let x = 0; x < ow; x++) {
      const insideText = x >= innerX0 && x <= innerX1 && y >= innerY0 && y <= innerY1;
      if (insideText) continue; // solo nos interesa el anillo, no el numero mismo
      const i = (y * ow + x) * 4;
      const isNonWhite = outer.data[i] < 230 || outer.data[i + 1] < 230 || outer.data[i + 2] < 230;
      if (isNonWhite) ringNonWhite++;
      ringTotal++;
    }
  }
  const ringFraction = ringTotal > 0 ? ringNonWhite / ringTotal : 0;
  // Un circulo/cuadro dibujado deja un anillo delgado de trazo - esperamos
  // una fraccion baja-moderada de pixeles no-blancos en el anillo (no toda
  // el area, que indicaria fondo de color solido en vez de un trazo).
  const isMarked = ringFraction > 0.04 && ringFraction < 0.35;
  const confidence = isMarked ? Math.min(1, 0.5 + ringFraction) : Math.max(0, 0.6 - ringFraction);
  return { isMarked, confidence };
}

/**
 * Punto de entrada del pipeline completo: recibe la imagen ya subida, la
 * configuracion de marcado (respuesta de la analista), y los meses a
 * procesar - devuelve, por mes, la lista de dias detectados con su
 * clasificacion de festivo y la confianza de esa lectura.
 */
export async function extractCalendarFromImage(
  imageFile: File,
  config: MarkingConfig,
  monthsToProcess: number[],
  onProgress?: (pct: number) => void
): Promise<Map<number, ExtractedDay[]>> {
  const [words, imageBitmap] = await Promise.all([recognizeCalendarImage(imageFile, onProgress), createImageBitmap(imageFile)]);

  const canvas = document.createElement('canvas');
  canvas.width = imageBitmap.width;
  canvas.height = imageBitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(imageBitmap, 0, 0);

  const { headers, dayCandidates } = splitWords(words);
  const byMonth = assignDaysToMonths(headers, dayCandidates);

  const result = new Map<number, ExtractedDay[]>();
  for (const month of monthsToProcess) {
    const boxes = byMonth.get(month) ?? [];
    const days: ExtractedDay[] = boxes.map((box) => {
      const { isMarked, confidence } = detectMarking(ctx, box, config);
      return { day: box.day, month, isMarked, confidence };
    });
    // Si el mismo numero aparece dos veces (ruido de OCR), se queda con la lectura de mayor confianza.
    const byDay = new Map<number, ExtractedDay>();
    for (const d of days) {
      const existing = byDay.get(d.day);
      if (!existing || d.confidence > existing.confidence) byDay.set(d.day, d);
    }
    result.set(month, Array.from(byDay.values()).sort((a, b) => a.day - b.day));
  }
  return result;
}
